import { BlogPost } from '@/types/blog';

export const post: BlogPost = {
  slug: 'deep-dive-how-teams-use-fordje-for-prospecting',
  title: 'Deep Dive: How Teams Use Fordje for Prospecting',
  excerpt: 'Every clean tech and housing project starts at the intersection of opportunity and risk. Learn how GAF Energy and other teams use Fordje to transform their prospecting workflow from days of research to actionable insights in under an hour.',
  content: `
    <p>Every clean tech and housing project starts at the intersection of opportunity and risk.</p>
    
    <p>Where should we go next and what's waiting for us when we get there?</p>
    
    <p>For most teams, "prospecting" means identifying new geographies that look promising. Maybe it's a fast-growing region, a new utility incentive, or a clear land and expand play. But even after you've flagged a market as interesting, there's still a murkier, more complex step ahead: understanding what's actually required to build there.</p>
    
    <p>And that's where it gets messy.</p>
    
    <p>Clean tech and housing teams alike face the same core challenge during prospecting: local city code clarity is almost never easy to find, and never in one place.</p>
    
    <p>Whether you're a solar company trying to validate new C&I markets, or a housing developer looking to expand into a new city or county, the questions are similar:</p>
    
    <ul>
      <li>What permits are required?</li>
      <li>Who governs zoning and setbacks?</li>
      <li>Are we allowed to build this product here?</li>
      <li>Is this rezoning-compatible if we need it to be?</li>
      <li>What's going to delay us?</li>
    </ul>
    
    <p>That's where Fordje comes in.</p>
    
    <h2>Prospecting in Practice: GAF Energy's Scalable Workflow</h2>
    
    <p>GAF Energy, a leader in building-integrated photovoltaics (BIPV), faced this exact challenge as they prepared to enter multiple new markets.</p>
    
    <p>Duncan Cleminshaw, their Senior Manager of Regulatory Compliance, shared how Fordje changed their prospecting workflow:</p>
    
    <p><em>"There's never any single repository that has all the information we need. The odds of the info being searchable on the building department's website is zero. And the odds of speaking to a human being who knows what the requirements are is... also zero."</em></p>
    
    <p>Traditionally, his team had to parse websites and call departments (with no guarantee of helpful answers), sometimes even submitting dummy projects just to get a response. It could take days to compile everything needed to move forward.</p>
    
    <p>With Fordje, that changed.</p>
    
    <p><em>"If I had to parse websites for four AHJs the old fashioned way, that could easily be 4 hours plus documenting and transcribing. Using Fordje allowed me to use a single source and complete the work in about 45 minutes."</em></p>
    
    <p>Using the Fordje platform, Duncan was able to:</p>
    
    <ul>
      <li>Search multiple jurisdictions for solar-specific permitting and code requirements</li>
      <li>Identify what licenses were required, how and where to submit permits, and whether same-day approvals were available</li>
      <li>Flag inconsistencies or vague areas in local code that could lead to approval delays later - and determine where they may need to engage local leaders early</li>
      <li>Compile everything into one place for internal handoff, without combing through scattered PDFs or waiting on returned calls</li>
    </ul>
    
    <p>The feedback was clear: it saved significant time and effort.</p>
    
    <p><em>"Fordje made my research waaaaaay easier."</em></p>
    
    <p>This level of upfront clarity doesn't just help solar companies - it applies just as easily to housing developers.</p>
    
    <p>Imagine you're evaluating a site in a new jurisdiction:</p>
    
    <ul>
      <li>Are setbacks compatible with the lot depth?</li>
      <li>Can you achieve your desired density under current FAR rules?</li>
      <li>Will rezoning require months of public hearings—or is it common in this district?</li>
      <li>Are infrastructure requirements (like sewer and road access) buried across multiple departments?</li>
    </ul>
    
    <p>With Fordje, you can answer those questions up front before you sink time into land acquisition or legal review.</p>
    
    <p>It becomes the foundation of your go/no-go decision.</p>
    
    <h2>Tools Developers Already Use</h2>
    
    <p>Teams already rely on tools like:</p>
    
    <ul>
      <li>Raptor Maps and PVcase for clean tech system siting and production modeling</li>
      <li>Acres, Regrid, or ArcGIS for mapping land and understanding parcel viability for housing or commercial buildings</li>
    </ul>
    
    <p>Fordje doesn't replace these. It adds the local code and permitting intelligence those platforms don't provide, layering in regulatory clarity alongside technical and economic viability.</p>
    
    <p>You can:</p>
    
    <ul>
      <li>Search multiple markets simultaneously</li>
      <li>Customize based on your product's unique constraints</li>
      <li>Export what you need to share with internal teams or partners</li>
    </ul>
    
    <h2>What's Next</h2>
    
    <p>Prospecting is messy by nature. But it doesn't have to be a black box.</p>
    
    <p>We're building tools that help teams make faster, more informed decisions without having to wait until the first permit rejection to find out what applies.</p>
    
    <p>In our next post, we'll walk through how teams use Fordje during site assessment once a parcel has been identified and it's time to ask: what will actually work here?</p>
  `,
  coverImage: '/lovable-uploads/b910d111-2b98-431f-993e-eb177eeb8c2e.png',
  date: '2025-06-03',
  readTime: 5,
  categories: ['Product', 'Case Study', 'Clean Tech', 'Housing'],
  author: {
    name: 'Gillian (Wildfire) Sowray',
    title: 'Co-founder & CEO',
    avatar: '/lovable-uploads/69c2731c-f0dc-41d3-8812-8b392d7302c2.png'
  }
};
